/**
 * noteModel — reading a matter note, once.
 *
 * A note arrives as `{label, value, type?}` plus whatever provenance the
 * backend happens to attach. The Notes tab used to GUESS at the value's type at
 * render time and had no concept of where a note came from at all, so a fact
 * the AI extracted from a case file and a fact a lawyer typed were
 * indistinguishable in the list.
 *
 * ORIGIN IS BEST-EFFORT, DELIBERATELY. There is no contractual origin field on
 * the note record today (see the mat-15 tracker card), so every plausible
 * shape is read and anything that does not clearly say is reported as
 * `unknown` rather than assigned to a side. The Notes tab only offers the
 * team/AI split when the payload actually distinguishes at least one note —
 * a split built on a guess would be worse than no split.
 */

export type NoteOrigin = 'user' | 'ai' | 'unknown';

export type NoteValueType = 'short_text' | 'long_text' | 'number' | 'date' | 'boolean';

/** Author strings that name a machine rather than a person. */
const MACHINE_AUTHORS = ['ai', 'pons', 'pons ai', 'system', 'assistant', 'automation', 'bot'];

const truthy = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

/**
 * Where a note came from.
 *
 * Explicit booleans win over author strings, and an author string is only
 * decisive when it either names a machine or names something that is plainly a
 * person. Everything else is `unknown`.
 */
export function noteOrigin(note: any): NoteOrigin {
  if (truthy(note?.is_ai_generated) || truthy(note?.ai_generated) || truthy(note?.generated)) {
    return 'ai';
  }
  if (truthy(note?.is_user_created) || truthy(note?.user_created)) return 'user';

  const source = String(note?.source ?? note?.origin ?? note?.created_by_type ?? '')
    .trim().toLowerCase();
  if (source) {
    if (MACHINE_AUTHORS.includes(source) || source.includes('ai') || source.includes('generat')) {
      return 'ai';
    }
    if (source === 'user' || source === 'manual' || source === 'human') return 'user';
  }

  const author = String(note?.created_by ?? note?.generated_by ?? note?.author ?? '')
    .trim().toLowerCase();
  if (!author) return 'unknown';
  if (MACHINE_AUTHORS.includes(author)) return 'ai';
  return 'user';
}

/** True when the payload can tell at least one note's origin apart. */
export function hasOriginSignal(notes: any[]): boolean {
  return notes.some((note) => noteOrigin(note) !== 'unknown');
}

/** A note's stable display author, or '' when the payload names nobody. */
export function noteAuthor(note: any): string {
  const raw = String(note?.created_by ?? note?.generated_by ?? note?.author ?? '').trim();
  if (!raw) return '';
  return MACHINE_AUTHORS.includes(raw.toLowerCase()) ? '' : raw;
}

/** When the note was last touched — updated wins over created. */
export function noteStamp(note: any): string {
  return String(note?.updated_at ?? note?.created_at ?? '');
}

/**
 * Any stored value, as a string.
 *
 * The extraction step returns arrays of party objects and nested fact objects
 * as often as it returns strings, and `String({})` is `[object Object]`.
 */
export function noteValueString(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'object' && item !== null) {
        // e.g. {name: "Emily", role: "petitioner"} → "Emily (petitioner)"
        if (item.name && item.role) return `${item.name} (${item.role})`;
        if (item.name) return item.name;
        return Object.values(item).join(': ');
      }
      return String(item);
    }).join(', ');
  }
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([key, entry]) => `${key.replace(/_/g, ' ')}: ${entry}`)
      .join(', ');
  }
  return String(value);
}

/** The fallback when a note carries no declared `type`. */
export function detectNoteValueType(value: string): NoteValueType {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'short_text';
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return 'date';
  if (!Number.isNaN(Number(trimmed))) return 'number';
  if (['true', 'false', 'yes', 'no'].includes(trimmed.toLowerCase())) return 'boolean';
  return trimmed.length > 50 ? 'long_text' : 'short_text';
}
